import type { SseFrame } from "@/shared/builder-session";
import {
  ensureChatListed,
  flushChatMessages,
  getChatMessages,
  listChatHistory,
  patchChatHistory,
  promoteChatHistory,
  saveChatMessages,
} from "@/features/chat/multi/history/chat-history";
import type { BuilderMessage } from "@/features/chat/builder/builder-types";
import { applyTranscriptFrame } from "@/features/chat/builder/sse/apply-transcript-frame";
import {
  hitlFramesFromDurableChunk,
  productFramesFromDurableChunk,
} from "@/features/chat/builder/sse/durable-chunk-hitl";

function append(sessionId: string, message: Omit<BuilderMessage, "id"> & { id?: string }) {
  const messages = getChatMessages(sessionId);
  messages.push({
    id: message.id ?? `bg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: message.type,
    text: message.text,
    ...(message.details !== undefined ? { details: message.details } : {}),
  });
  saveChatMessages(sessionId, messages);
  if (listChatHistory().some((item) => item.id === sessionId)) {
    patchChatHistory(sessionId, { messages });
  } else {
    promoteChatHistory({ sessionId, messages });
  }
}

function endBackgroundStream(sessionId: string) {
  flushChatMessages(sessionId);
}

function upsertBackgroundCard(
  sessionId: string,
  type: "plan" | "intent",
  text: string,
  details: unknown,
) {
  const messages = getChatMessages(sessionId);
  const intentId =
    type === "intent" && details && typeof details === "object"
      ? String((details as { id?: string }).id ?? "").trim()
      : "";

  let existingIdx = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.type !== type) continue;
    if (type === "intent" && intentId) {
      const detailsId = String((message.details as { id?: string } | undefined)?.id ?? "").trim();
      if (detailsId && detailsId !== intentId) continue;
    }
    existingIdx = index;
    break;
  }

  const bubble: BuilderMessage = {
    id: existingIdx >= 0 ? messages[existingIdx]!.id : `bg-${type}-${Date.now()}`,
    type,
    text,
    details,
  };
  if (existingIdx >= 0) messages[existingIdx] = bubble;
  else messages.push(bubble);
  saveChatMessages(sessionId, messages);
  patchChatHistory(sessionId, { messages });
}

/**
 * Background transcript only. Live badges are owned by applyChatFrame in
 * multiplex/durable transport — do not re-apply flags here.
 */
export function persistBackgroundFrame(sessionId: string, frame: SseFrame) {
  const id = sessionId.trim();
  if (!id) return;

  const { event, data } = frame;

  // Soft HITL parks / tool progress arrive as raw `chunk` — expand like
  // handle-sse-frame so background chats still get badges and tool rows.
  if (event === "chunk") {
    for (const hitl of hitlFramesFromDurableChunk(data)) {
      persistBackgroundFrame(id, hitl);
    }
    for (const product of productFramesFromDurableChunk(data)) {
      persistBackgroundFrame(id, product);
    }
    return;
  }

  if (event === "session") {
    ensureChatListed({
      sessionId: id,
      messages: getChatMessages(id),
      live: true,
    });
    return;
  }

  if (event === "stream-start") {
    return;
  }

  const result = applyTranscriptFrame(frame, {
    append: (type, text, details) => {
      append(id, {
        type: type as BuilderMessage["type"],
        text,
        details,
      });
    },
    appendTextDelta: (text) => {
      const messages = getChatMessages(id);
      const last = messages.at(-1);
      if (last?.type === "assistant") {
        last.text = `${last.text}${text}`;
        saveChatMessages(id, messages);
        patchChatHistory(id, { messages });
      } else {
        append(id, { type: "assistant", text });
      }
    },
    upsertPlan: (details) => {
      upsertBackgroundCard(id, "plan", "Plan ready for approval.", details);
    },
  });

  if (result.handled) {
    if (result.endStream) endBackgroundStream(id);
    return;
  }

  if (event === "intent" && data) {
    const unknowns = (data as { intent?: { unknowns?: unknown[] } }).intent?.unknowns;
    if (Array.isArray(unknowns) && unknowns.length > 0) return;
    // Update/append only — never strip earlier intents (that jumped the card to the tail).
    upsertBackgroundCard(id, "intent", "Intent ready for approval.", data);
    return;
  }

  if (event === "draft-saved") {
    const messages = getChatMessages(id);
    if (
      !messages.some((m) => (m.details as { kind?: string } | undefined)?.kind === "draft-saved")
    ) {
      messages.push({
        id: `bg-draft-saved-${Date.now()}`,
        type: "status",
        text: "Draft saved",
        details: { kind: "draft-saved" },
      });
      saveChatMessages(id, messages);
      patchChatHistory(id, { messages });
    }
    return;
  }

  // Ignore builder-only events in background (graph, phase-change, queues).
}
